import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import LatestNews from "./LatestNews";
import ContactSection from "./ContactSection";
import WhatWeDo from "./WhatWeDo";

/* ========================
   TYPES
======================== */

interface Category {
  id: number;
  name: string;
  image: string;
  description: string;
  whatsappMessage: string;
  category_name: string;
}

/* ========================
   COMPONENT
======================== */

const Hardware: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const navigate = useNavigate();
  const isSubCategoryHoveredRef = useRef(false);

  const subCategorySliderRef = useRef<HTMLDivElement | null>(null);

  const scrollSubCategorySlider = (direction: "left" | "right") => {
    if (!subCategorySliderRef.current) return;

    subCategorySliderRef.current.scrollBy({
      left: direction === "left" ? -260 : 260,
      behavior: "smooth",
    });
  };

  /* ========================
   SLIDE CATEGORIES
======================== */

  useEffect(() => {
    const slider = subCategorySliderRef.current;

    if (!slider || categories.length === 0) return;

    let animationFrameId: number;
    let lastTime: number | null = null;

    const speed = 0.12;

    slider.scrollLeft = 0;

    const autoScroll = (currentTime: number) => {
      if (lastTime === null) {
        lastTime = currentTime;
      }

      const rawDeltaTime = currentTime - lastTime;
      const deltaTime = Math.min(rawDeltaTime, 32);

      lastTime = currentTime;

      if (
        !isSubCategoryHoveredRef.current &&
        slider.scrollWidth > slider.clientWidth
      ) {
        slider.scrollLeft += deltaTime * speed;

        const maxScrollLeft = slider.scrollWidth - slider.clientWidth;
        const halfScrollWidth = slider.scrollWidth / 2;

        const resetPoint =
          maxScrollLeft >= halfScrollWidth ? halfScrollWidth : maxScrollLeft;

        if (slider.scrollLeft >= resetPoint - 1) {
          slider.scrollLeft = 0;
        }
      }

      animationFrameId = requestAnimationFrame(autoScroll);
    };

    animationFrameId = requestAnimationFrame(autoScroll);

    return () => cancelAnimationFrame(animationFrameId);
  }, [categories.length]);

  /* ========================
     FETCH CATEGORIES
  ======================== */

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_LYRAZONE_BASE_URL}/categories`,
        );
        setCategories(response.data.categories);
      } catch (err) {
        console.error(err);
      }
    };

    fetchData();
  }, []);

  /* ========================
     GROUP CATEGORIES
  ======================== */

  const groupedCategories = categories.reduce<Record<string, Category[]>>(
    (acc, category) => {
      if (!acc[category.category_name]) {
        acc[category.category_name] = [];
      }

      acc[category.category_name].push(category);
      return acc;
    },
    {},
  );

  /* ========================
     PRODUCT NAVIGATION
  ======================== */

  const productData = (subCategoryId: number, subCategoryName: string) => {
    navigate(
      `/subproducts?id=${subCategoryId}&name=${encodeURIComponent(
        subCategoryName,
      )}`,
    );
  };

  /* ========================
     RENDER
  ======================== */

  return (
    <>
      {/* ====================================================
                       HOME-P
====================================================== */}

      <div id="home-p" className="home-p pages-head7 text-center">
        <div className="container"></div>
      </div>

      {/* ====================================================
                      BUSINESS-GROWTH-P1
====================================================== */}

      <section
        id="business-growth-p1"
        className="hardware-subcategory-strip-section"
      >
        <div
          className="hardware-subcategory-strip-wrap"
          onMouseEnter={() => {
            isSubCategoryHoveredRef.current = true;
          }}
          onMouseLeave={() => {
            isSubCategoryHoveredRef.current = false;
          }}
        >
          <button
            type="button"
            className="hardware-subcategory-arrow hardware-subcategory-arrow-left"
            onClick={() => scrollSubCategorySlider("left")}
            aria-label="Scroll subcategories left"
          >
            ‹
          </button>

          <div
            ref={subCategorySliderRef}
            className="hardware-subcategory-strip"
          >
            {[...categories, ...categories].map((subCategory, index) => (
              <button
                key={`${subCategory.id}-${index}`}
                type="button"
                className="hardware-subcategory-item"
                onClick={() => productData(subCategory.id, subCategory.name)}
                title={subCategory.name}
              >
                <span className="hardware-subcategory-icon">
                  <img
                    src={`${
                      import.meta.env.VITE_LYRAZONE_IMAGE_URL
                    }/uploads/subcategories/${subCategory.image}`}
                    alt={subCategory.name}
                  />
                </span>

                <span className="hardware-subcategory-name">
                  {subCategory.name}
                </span>
              </button>
            ))}
          </div>

          <button
            type="button"
            className="hardware-subcategory-arrow hardware-subcategory-arrow-right"
            onClick={() => scrollSubCategorySlider("right")}
            aria-label="Scroll subcategories right"
          >
            ›
          </button>
        </div>
      </section>

      {/* ====================================================
                         PRODUCT SECTION
====================================================== */}

      <section id="comp-offer" className="hardware-shop-section">
        <div className="container-fluid">
          {Object.entries(groupedCategories).map(
            ([category, items], catIndex) => (
              <div className="hardware-shop-category-block" key={catIndex}>
                <div className="hardware-shop-category-header wow fadeInUp">
                  <h2>{category}</h2>
                  <div className="hardware-shop-header-line"></div>
                </div>

                <div className="hardware-shop-card-row">
                  {items.map((subCategory, index) => (
                    <a
                      key={index}
                      href={`/subproducts?id=${subCategory.id}&name=${encodeURIComponent(
                        subCategory.name,
                      )}`}
                      className="hardware-shop-card wow fadeInUp"
                      data-wow-delay="0.2s"
                      onClick={(e) => {
                        e.preventDefault();
                        productData(subCategory.id, subCategory.name);
                      }}
                    >
                      <div className="hardware-shop-image-wrap">
                        <img
                          src={`${
                            import.meta.env.VITE_LYRAZONE_IMAGE_URL
                          }/uploads/subcategories/${subCategory.image}`}
                          alt={subCategory.name}
                        />

                        <span className="hardware-shop-discount-badge">
                          -{index % 2 === 0 ? "33" : "20"}%
                        </span>

                        <span className="hardware-shop-play-badge">›</span>

                        <div className="hardware-shop-mini-tags">
                          <span>
                            FREE
                            <br />
                            SUPPORT
                          </span>
                          <span>
                            ICT
                            <br />
                            HARDWARE
                          </span>
                          <span>
                            LYRA
                            <br />
                            ZONE
                          </span>
                        </div>
                      </div>

                      <div className="hardware-shop-content">
                        <div className="hardware-shop-label-row">
                          <span className="hardware-shop-label preferred">
                            Preferred
                          </span>
                          <span className="hardware-shop-label local">
                            Local Delivery
                          </span>
                        </div>

                        <h3>{subCategory.name}</h3>

                        <p className="hardware-shop-desc">
                          {subCategory.description}
                        </p>

                        <div className="hardware-shop-bottom">
                          <span className="hardware-shop-price">
                            View Products
                          </span>
                          <span className="hardware-shop-sold">
                            {index % 2 === 0 ? "500+" : "1k+"} viewed
                          </span>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            ),
          )}
        </div>
      </section>

      <WhatWeDo />
      <ContactSection />
      <LatestNews />
    </>
  );
};

/* ========================
   EXTRA COMPONENT
======================== */

// const FinancialContent: React.FC<{
//   title: string;
//   description: string;
// }> = ({ title, description }) => (
//   <div className="financial-p1-cont">
//     <a href="#">
//       <b>Richtech Technology</b>
//     </a>
//     <h3>{title}</h3>
//     <p>{description}</p>
//     <button className="btn btn-general btn-white" role="button">
//       Contact Us
//     </button>
//   </div>
// );

export default Hardware;
